import { useEffect, useRef } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Close, Stars } from 'blaze-ui/icons'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useCardDismissal } from 'hooks/queries/cardDismissals/useCardDismissal'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { VisualStylesForExistingUsersModal } from 'components/VisualStylesForExistingUsersModal/VisualStylesForExistingUsersModal'
import Styles from './useVisualStylesForExistingUsers.module.scss'

export const useVisualStylesForExistingUsers = () => {
  const { openModal } = useModals()
  const hasChecked = useRef(false)

  // Feature flags

  // Workspace data
  const workspace = useCurrentWorkspace()
  const isAutopilotWorkspace = workspace?.is_virtual_marketer ?? false
  const isFreemiumWorkspace = workspace?.is_ap_freemium ?? false

  // Subscription data
  const { data: currentSubscription } = useCurrentPlan()
  const isCanceled = currentSubscription?.status === 'canceled'

  // Card dismissal for modal (persisted)
  const {
    data: modalDismissalData,
    isLoading: isModalDismissalLoading,
    dismissCard: dismissModalCard,
  } = useCardDismissal('visual_styles_existing_users_modal')
  const isModalDismissed = modalDismissalData?.is_dismissed ?? false

  // Card dismissal for banner (persisted separately)
  const {
    data: bannerDismissalData,
    isLoading: isBannerDismissalLoading,
    dismissCard: dismissBannerCard,
  } = useCardDismissal('visual_styles_existing_users_banner')
  const isBannerDismissed = bannerDismissalData?.is_dismissed ?? false

  // Brand kit data to check if visual style is already set
  const { data: brandPlan, isLoading: isBrandPlanLoading } = useCurrentBrandPlan()
  const brandKitId = brandPlan?.brand_kit?.id

  const { data: brandKitData, isLoading: isBrandKitLoading } = useBrandKit(brandKitId, {
    enabled: !!brandKitId,
  })
  const hasVisualStyleSet = !!brandKitData?.brand_kit?.brand_visual_kit?.visual_style_id

  // Loading state
  const isLoading =
    isModalDismissalLoading || isBannerDismissalLoading || isBrandPlanLoading || (!!brandKitId && isBrandKitLoading)

  // Base criteria for showing modal/banner
  const meetsBaseCriteria =
    isAutopilotWorkspace && !isFreemiumWorkspace && !isCanceled && !hasVisualStyleSet && !isLoading

  // Check criteria for showing modal
  const shouldShowModal = meetsBaseCriteria && !isModalDismissed

  // Check criteria for showing banner (same logic, separate dismissal)
  const shouldShowBanner = meetsBaseCriteria && !isBannerDismissed

  const handleOpenModal = () => {
    openModal(VisualStylesForExistingUsersModal)
  }

  const handleDismissBanner = () => {
    dismissBannerCard()
  }

  useEffect(() => {
    // Only check once and when not loading
    if (hasChecked.current || isLoading) return

    if (shouldShowModal) {
      hasChecked.current = true
      // Dismiss the card so modal won't show again
      dismissModalCard()
      openModal(VisualStylesForExistingUsersModal)
    }
  }, [shouldShowModal, isLoading, openModal, dismissModalCard])

  const visualStylesBanner = (
    <div className={Styles.container}>
      <div className={Styles.leftContent}>
        <Stars size={20} color="var(--dark-80)" />
        <Text variant="smallList" color="var(--dark-80)">
          Turn on stylized videos and images for truly beautiful content that performs
        </Text>
      </div>
      <div className={Styles.rightContent}>
        <Button size="sm" variant="ghost" onPress={handleOpenModal}>
          Learn More
        </Button>
        <Button size="sm" variant="ghost" frontIcon={Close} onPress={handleDismissBanner} color="var(--dark-80)" />
      </div>
    </div>
  )

  return {
    showBanner: shouldShowBanner,
    visualStylesBanner,
    shouldShowModal,
    isLoading,
  }
}
